import { useEffect, useMemo, useRef, useState } from 'react';
import contentSectionService from '../../services/contentSectionService';
import { getProductImageUrl } from '../../utils/formatters';
import { SECTION_TYPE } from './constants';

const useDebounced = (value, ms) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
};

const Thumb = ({ url, name }) => {
  const src = getProductImageUrl(url);
  return src ? <img className="cs-thumb" src={src} alt={name || ''} loading="lazy" /> : <span className="cs-thumb cs-thumb--empty" />;
};

/**
 * Searchable selector + drag-and-drop ordered list of pinned products.
 * The search only returns products that are storefront-eligible AND match the section's filters,
 * so an ineligible product can't be pinned from here (the server re-checks on save).
 */
const ProductPicker = ({ sectionType, filters, pinned, onChange, isRtl }) => {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const seq = useRef(0);

  const debouncedSearch = useDebounced(search, 300);
  const filterKey = useMemo(
    () => JSON.stringify(filters.filter((f) => f.value !== '').map((f) => [f.type, f.value])),
    [filters],
  );
  const pinnedKey = pinned.map((p) => p.productId).join(',');

  useEffect(() => {
    const mine = ++seq.current;
    setLoading(true);
    contentSectionService
      .productLookup({
        sectionType,
        filters: filters.filter((f) => f.value !== '').map((f) => ({ type: f.type, value: f.value })),
        search: debouncedSearch || null,
        excludeIds: pinned.map((p) => p.productId),
        take: 20,
      })
      .then((r) => {
        if (mine === seq.current) setResults(r || []);
      })
      .catch(() => {
        if (mine === seq.current) setResults([]);
      })
      .finally(() => {
        if (mine === seq.current) setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, filterKey, pinnedKey, sectionType]);

  const add = (p) =>
    onChange([
      ...pinned,
      {
        productId: p.productId,
        nameEn: p.nameEn,
        nameAr: p.nameAr,
        brandNameEn: p.brandNameEn,
        brandNameAr: p.brandNameAr,
        imageUrl: p.imageUrl,
        isEligible: true,
      },
    ]);

  const move = (from, to) => {
    if (to < 0 || to >= pinned.length || from === to) return;
    const next = [...pinned];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange(next);
  };

  const isCustom = sectionType === SECTION_TYPE.CUSTOM;
  const name = (p) => (isRtl ? p.nameAr || p.nameEn : p.nameEn);
  const brand = (p) => (isRtl ? p.brandNameAr || p.brandNameEn : p.brandNameEn);

  return (
    <div className="cs-picker">
      <div className="cs-picker-col">
        <div className="cs-col-title">
          {isRtl ? 'المنتجات المثبّتة (بالترتيب)' : 'Pinned products (in order)'}
          <span className="badge badge-neutral">{pinned.length}</span>
        </div>
        {pinned.length === 0 ? (
          <div className="cs-empty">
            {isCustom
              ? isRtl ? 'اختر المنتجات من القائمة المجاورة. هذه هي كل محتويات القسم.' : 'Pick products from the list. They are the whole section.'
              : isRtl ? 'اختياري: ثبّت منتجات لتظهر أولاً قبل المنتجات التلقائية.' : 'Optional: pin products to show first, before the automatic ones.'}
          </div>
        ) : (
          <ol className="cs-pinned">
            {pinned.map((p, i) => (
              <li
                key={p.productId}
                className={`cs-pinned-item ${dragIndex === i ? 'is-dragging' : ''} ${overIndex === i && dragIndex !== null && dragIndex !== i ? 'is-over' : ''} ${p.isEligible === false ? 'is-ineligible' : ''}`}
                draggable
                onDragStart={(e) => {
                  setDragIndex(i);
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', String(i));
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  setOverIndex(i);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (dragIndex !== null) move(dragIndex, i);
                  setDragIndex(null);
                  setOverIndex(null);
                }}
                onDragEnd={() => {
                  setDragIndex(null);
                  setOverIndex(null);
                }}
              >
                <span className="cs-grip" aria-hidden="true">⋮⋮</span>
                <span className="cs-order">{i + 1}</span>
                <Thumb url={p.imageUrl} name={name(p)} />
                <div className="cs-pinned-info">
                  <div className="cs-pinned-name">{name(p)}</div>
                  <div className="cs-pinned-meta">
                    {brand(p)}
                    {p.isEligible === false ? (
                      <span className="badge badge-danger">{isRtl ? 'غير متاح/لا يطابق' : 'Unavailable / no match'}</span>
                    ) : null}
                  </div>
                </div>
                <div className="cs-row-actions">
                  <button type="button" className="btn btn-ghost btn-icon" onClick={() => move(i, i - 1)} disabled={i === 0} aria-label="Up">↑</button>
                  <button type="button" className="btn btn-ghost btn-icon" onClick={() => move(i, i + 1)} disabled={i === pinned.length - 1} aria-label="Down">↓</button>
                  <button type="button" className="btn btn-ghost btn-icon cs-danger" onClick={() => onChange(pinned.filter((x) => x.productId !== p.productId))} aria-label="Remove">×</button>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>

      <div className="cs-picker-col">
        <div className="cs-col-title">{isRtl ? 'إضافة منتجات' : 'Add products'}</div>
        <input
          className="form-input"
          type="search"
          placeholder={isRtl ? 'ابحث بالاسم أو الماركة…' : 'Search by name or brand…'}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="cs-hint">
          {isCustom
            ? isRtl ? 'يمكن اختيار أي منتج منشور.' : 'Any published product can be picked.'
            : isRtl ? 'تظهر فقط المنتجات المتاحة التي تطابق شروط القسم.' : 'Only available products matching the section conditions are listed.'}
        </div>
        <ul className="cs-results" aria-busy={loading}>
          {results.map((p) => (
            <li key={p.productId} className="cs-result">
              <Thumb url={p.imageUrl} name={name(p)} />
              <div className="cs-pinned-info">
                <div className="cs-pinned-name">{name(p)}</div>
                <div className="cs-pinned-meta">{brand(p)}</div>
              </div>
              <button type="button" className="btn btn-secondary btn-small" onClick={() => add(p)}>
                {isRtl ? 'تثبيت' : 'Pin'}
              </button>
            </li>
          ))}
          {!loading && results.length === 0 ? <li className="cs-empty">{isRtl ? 'لا نتائج.' : 'No results.'}</li> : null}
        </ul>
      </div>
    </div>
  );
};

export default ProductPicker;
